// Ashen Host content (Bible AH-01..AH-25). Bible-specified where stated; TEST VALUES
// otherwise. Core HP scaled by the owner's ~0.53 TTK factor (6,900 → 3,650).

import type {
  AbilityDef,
  BossExtras,
  BuildingDef,
  CardDef,
  RitualDef,
  SpellDef,
  TrapDef,
  UnitDef,
} from './types';

// ---------------------------------------------------------------- units

export const AH_UNITS: UnitDef[] = [
  {
    id: 'u-ah-worker',
    name: 'Gravedigger',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['worker'],
    squadMemberCount: 1,
    maxHealth: 48,
    armor: 'none',
    moveSpeed: 3.2,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    ui: { color: 0x8a8090, scale: 0.55, label: 'Gravedigger' },
  },
  {
    // temporary shade summoned by the Ossuary Throne, Hollow King, and resurrections
    id: 'u-ah-shade',
    name: 'Grave Shade',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['raider'],
    squadMemberCount: 3,
    maxHealth: 220, // TEST — expendable by design
    armor: 'none',
    moveSpeed: 4.2,
    attack: { damage: 26, period: 0.9, range: 1.4 },
    targetPriority: ['squad', 'worker'],
    abilities: [],
    command: 0, // conjured, not commanded
    captureWeight: 0,
    ui: { color: 0x77d97e, scale: 0.7, label: 'Shade' },
  },
  {
    id: 'u-ah-reavers',
    name: 'Gravebound Reavers',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['raider', 'frontline'],
    squadMemberCount: 7, // bible AH-09
    maxHealth: 640, // TEST
    armor: 'light',
    moveSpeed: 3.9,
    attack: { damage: 36, period: 0.9, range: 1.5 },
    targetPriority: ['worker', 'squad', 'building'],
    abilities: ['ab-ah-hunger-route'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0x6a5d70, scale: 0.85, label: 'Reavers' },
  },
  {
    id: 'u-ah-ghoul-pack',
    name: 'Ghoul Pack',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 8, // bible AH-10
    maxHealth: 560, // TEST — cheapest combat squad
    armor: 'none',
    moveSpeed: 3.6,
    attack: { damage: 30, period: 1.0, range: 1.4 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-ah-scavenge-flesh'],
    command: 2,
    captureWeight: 1,
    ui: { color: 0x7a6d80, scale: 0.75, label: 'Ghouls' },
  },
  {
    id: 'u-ah-bone-archers',
    name: 'Bone Archers',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['ranged'],
    squadMemberCount: 6, // bible AH-11
    maxHealth: 520, // TEST
    armor: 'none',
    moveSpeed: 3.1,
    attack: { damage: 34, period: 1.3, range: 11, projectileSpeed: 26 },
    targetPriority: ['squad', 'worker', 'large'],
    abilities: ['ab-ah-splinter-the-weak'],
    command: 3,
    captureWeight: 1,
    ui: { color: 0xc8c0b0, scale: 0.85, label: 'Bone Archers' },
  },
  {
    id: 'u-ah-corpse-carrier',
    name: 'Corpse Carrier',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible AH-12: carrier beast + two handlers
    maxHealth: 480, // TEST
    armor: 'light',
    moveSpeed: 3.0,
    attack: { damage: 12, period: 1.5, range: 1.6 },
    targetPriority: ['squad'],
    abilities: ['ab-ah-gather-the-dead'],
    command: 3,
    captureWeight: 0.5,
    ui: { color: 0x8a7a6a, scale: 0.9, label: 'Carrier' },
  },
  {
    id: 'u-ah-flesh-guard',
    name: 'Flesh Guard',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 4, // bible AH-13
    maxHealth: 820, // TEST — durable escort infantry
    armor: 'heavy',
    moveSpeed: 2.9,
    attack: { damage: 34, period: 1.1, range: 1.6 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-ah-stitch-closed'],
    command: 4,
    captureWeight: 1,
    ui: { color: 0x9a6a6a, scale: 0.95, label: 'Flesh Guard' },
  },
  {
    id: 'u-ah-sacrificial-brute',
    name: 'Sacrificial Brute',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['frontline', 'raider'],
    squadMemberCount: 1,
    maxHealth: 1150, // TEST
    armor: 'moderate',
    moveSpeed: 3.2,
    attack: { damage: 78, period: 1.3, range: 2.0 },
    targetPriority: ['squad', 'worker', 'building'],
    abilities: ['ab-ah-gore-rush'],
    command: 4,
    captureWeight: 1,
    ui: { color: 0x8a5a5a, scale: 1.3, label: 'Brute' },
  },
  {
    id: 'u-ah-soul-collector',
    name: 'Soul Collector',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['worker'],
    squadMemberCount: 1,
    maxHealth: 40, // bible AH-15: low physical health
    armor: 'none',
    moveSpeed: 3.4,
    targetPriority: [],
    abilities: [],
    command: 0,
    captureWeight: 0,
    workerVariant: { buildMult: 1, repairMult: 1, carryMult: 1.3 },
    ui: { color: 0x9ad8e0, scale: 0.6, label: 'Collector' },
  },
  {
    id: 'u-ah-gore-engine',
    name: 'Gore Engine',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['siege'],
    squadMemberCount: 1,
    maxHealth: 1700, // TEST
    armor: 'heavy',
    moveSpeed: 2.1,
    attack: { damage: 95, period: 1.9, range: 8, projectileSpeed: 22, structureMult: 1.5 },
    targetPriority: ['building', 'trap', 'siege', 'squad'],
    abilities: ['ab-ah-blood-pressure', 'ab-ah-rupture-spray'],
    command: 8,
    captureWeight: 0.5,
    ui: { color: 0x7a4a4a, scale: 1.35, label: 'Gore Engine' },
  },
  {
    id: 'u-ah-soul-lanterns',
    name: 'Soul Lanterns',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['support'],
    squadMemberCount: 3, // bible v0.2.4 AH-04
    maxHealth: 300, // TEST
    armor: 'none',
    moveSpeed: 3.2,
    attack: { damage: 10, period: 1.6, range: 7, projectileSpeed: 18 }, // TEST — token wisp bolt
    targetPriority: ['squad'],
    abilities: ['ab-ah-gather-last-breath', 'ab-ah-wisp-screen'],
    command: 2,
    captureWeight: 0.5,
    maxActiveUnits: 3, // bible v0.2.4 AH-04: maximum three active
    ui: { color: 0xd0907a, scale: 0.7, label: 'Lanterns' },
  },
  {
    id: 'u-ah-wailing-brides',
    name: 'Wailing Brides',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['support', 'ranged'],
    squadMemberCount: 4, // bible v0.2.4 AH-07
    maxHealth: 460, // TEST
    armor: 'none',
    moveSpeed: 3.0,
    attack: { damage: 22, period: 1.4, range: 9, projectileSpeed: 20 }, // TEST
    targetPriority: ['squad', 'large'],
    abilities: ['ab-ah-sorrow-wave', 'ab-ah-haunting-bond'],
    command: 4,
    captureWeight: 1,
    maxActiveUnits: 2, // bible v0.2.4 AH-07: maximum two active
    ui: { color: 0xb8a8c8, scale: 0.9, label: 'Brides' },
  },
  {
    id: 'u-ah-dread-cavalry',
    name: 'Dread Cavalry',
    faction: 'ashen-host',
    sizeTag: 'medium',
    roleTags: ['raider', 'frontline'],
    squadMemberCount: 4, // bible v0.2.4 AH-09
    maxHealth: 620, // TEST
    armor: 'light',
    moveSpeed: 4.6, // TEST — fastest AH ground squad
    attack: { damage: 44, period: 1.1, range: 1.8 },
    targetPriority: ['worker', 'siege', 'squad'],
    abilities: ['ab-ah-terror-charge', 'ab-ah-ride-down-the-weak', 'ab-cavalry-charge'],
    command: 5,
    captureWeight: 1,
    ui: { color: 0x704a5a, scale: 1.0, label: 'Dread Cavalry' },
  },
  {
    // bible v0.2.4 AH-12: flying squad — no flight mechanic, modeled as a fast
    // terrain-bound skirmisher with dive-flavored abilities (deviation)
    id: 'u-ah-bonewyrm-riders',
    name: 'Bonewyrm Riders',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['raider', 'ranged'],
    squadMemberCount: 3, // bible v0.2.4 AH-12
    maxHealth: 1050, // TEST
    armor: 'light',
    moveSpeed: 4.2, // TEST — approximates over-terrain mobility
    attack: { damage: 55, period: 1.4, range: 6, projectileSpeed: 20, structureMult: 1.25 }, // TEST — Acid Rain repair pressure folded into structureMult
    targetPriority: ['worker', 'siege', 'building', 'squad'],
    abilities: ['ab-ah-acid-rain', 'ab-ah-corpse-drop', 'ab-cavalry-charge'],
    command: 6,
    captureWeight: 0.5,
    ui: { color: 0xb0a890, scale: 1.25, label: 'Bonewyrms' },
  },
  {
    id: 'u-ah-plague-apostles',
    name: 'Plague Apostles',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['support', 'ranged'],
    squadMemberCount: 3, // bible v0.2.4 AH-13
    maxHealth: 430, // TEST
    armor: 'none',
    moveSpeed: 2.9,
    attack: { damage: 26, period: 1.5, range: 8, projectileSpeed: 18 }, // TEST
    targetPriority: ['squad', 'large'],
    abilities: ['ab-ah-contagion', 'ab-ah-unclean-miracle'],
    command: 4,
    captureWeight: 1,
    maxActiveUnits: 2, // bible v0.2.4 AH-13: maximum two active
    ui: { color: 0x7a8a5a, scale: 0.9, label: 'Apostles' },
  },
  {
    id: 'u-ah-ebon-executioners',
    name: 'Ebon Executioners',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 4, // bible v0.2.4 AH-14
    maxHealth: 700, // TEST
    armor: 'moderate',
    moveSpeed: 3.3,
    attack: { damage: 50, period: 1.2, range: 1.7 }, // TEST
    targetPriority: ['squad', 'large', 'giant'],
    abilities: ['ab-ah-decapitate', 'ab-ah-no-return'],
    command: 5,
    captureWeight: 1,
    ui: { color: 0x3a3a44, scale: 0.95, label: 'Executioners' },
  },
  {
    id: 'u-ah-cinder-revenants',
    name: 'Cinder Revenants',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['ranged', 'raider'],
    squadMemberCount: 5, // bible v0.2.4 AH-15
    maxHealth: 520, // TEST
    armor: 'light',
    moveSpeed: 3.5,
    // damageTag 'fire' folds their ember flavor into the attack (suppresses Verdant regen)
    attack: { damage: 30, period: 1.2, range: 8, projectileSpeed: 22, damageTag: 'fire' }, // TEST
    targetPriority: ['squad', 'worker'],
    abilities: ['ab-ah-ashstep', 'ab-ah-immolate-remains'],
    command: 4,
    captureWeight: 1,
    ui: { color: 0x9a5a45, scale: 0.85, label: 'Revenants' },
  },
  {
    id: 'u-ah-grave-leviathan',
    name: 'Grave Leviathan',
    faction: 'ashen-host',
    sizeTag: 'giant',
    roleTags: ['frontline'],
    squadMemberCount: 1, // bible v0.2.4 AH-16
    maxHealth: 2250, // TEST — giant HP pass: −25% so focused fire pays off
    armor: 'heavy',
    moveSpeed: 2.4,
    attack: { damage: 120, period: 1.8, range: 2.6, structureMult: 1.2 }, // TEST
    targetPriority: ['squad', 'building'],
    abilities: ['ab-ah-corpse-wake', 'ab-ah-swallow-the-faithful', 'ab-ah-leviathan-charge'],
    command: 10,
    captureWeight: 0.5,
    ui: { color: 0x7a7060, scale: 2.1, label: 'Leviathan' },
  },
  {
    id: 'u-ah-deathless-legion',
    name: 'Deathless Legion',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 8, // bible v0.2.4 AH-17
    maxHealth: 1200, // TEST — endgame phalanx
    armor: 'heavy',
    moveSpeed: 2.8,
    attack: { damage: 60, period: 1.1, range: 1.6 }, // TEST
    targetPriority: ['squad', 'building'],
    abilities: ['ab-ah-reform-the-ranks', 'ab-ah-eternal-march'],
    command: 8,
    captureWeight: 1,
    ui: { color: 0xa89a78, scale: 0.9, label: 'Legion' },
  },
  {
    id: 'boss-ah-veyr',
    name: 'Carrion Prince Veyr',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['boss', 'raider'],
    squadMemberCount: 1,
    maxHealth: 750, // halved from previous prototype value per owner feedback
    armor: 'moderate',
    moveSpeed: 3.8,
    attack: { damage: 82, period: 1.2, range: 2.2 },
    targetPriority: ['worker', 'squad', 'building'],
    abilities: ['ab-ah-carrion-dive', 'ab-ah-feast-of-motion'],
    command: 7,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x6a4d80, scale: 1.7, label: 'Veyr' },
  },
  {
    id: 'boss-ah-gravebound-colossus',
    name: 'Gravebound Colossus',
    faction: 'ashen-host',
    sizeTag: 'giant',
    roleTags: ['boss', 'siege'],
    squadMemberCount: 1,
    maxHealth: 1800, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 1.9,
    attack: { damage: 130, period: 1.9, range: 3.0, structureMult: 1.5 },
    targetPriority: ['building', 'squad'],
    abilities: ['ab-ah-grave-quake', 'ab-ah-colossus-crush'],
    command: 13,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x5a5d66, scale: 2.2, label: 'Colossus' },
  },
  {
    id: 'boss-ah-hollow-king',
    name: 'The Hollow King',
    faction: 'ashen-host',
    sizeTag: 'giant',
    roleTags: ['boss', 'frontline'],
    squadMemberCount: 1,
    maxHealth: 2550, // halved from previous prototype value per owner feedback
    armor: 'heavy',
    moveSpeed: 2.2,
    attack: { damage: 120, period: 1.7, range: 3.0 },
    targetPriority: ['squad', 'building'],
    abilities: ['ab-ah-court-of-the-fallen', 'ab-ah-empty-throne', 'ab-ah-king-without-end', 'ab-ah-king-crush'],
    command: 19,
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x4a4d5a, scale: 2.5, label: 'Hollow King' },
  },
  // ---- Core Annex System (owner spec 2026-07-13) --------------------------
  {
    // Carrion Procession spawn: fragile funeral carriage. E: Raise Grave Beacon.
    id: 'u-ah-bone-hearse',
    name: 'Bone Hearse',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['support'],
    squadMemberCount: 1,
    maxHealth: 700, // TEST — spec: fragile while moving; intercepting it is counterplay
    armor: 'light',
    moveSpeed: 2.2, // TEST
    targetPriority: [],
    abilities: [],
    command: 0, // deployable building-carrier, not an army unit
    captureWeight: 0,
    deploysInto: {
      buildingId: 'b-ah-grave-beacon',
      assembleSeconds: 50, // spec: after 50 s it becomes a free Grave Beacon
      maxDeployed: 1, // spec: maximum one active Beacon from this annex
      witherSeconds: 12, // TEST
      label: 'Raise Grave Beacon',
    },
    ui: { color: 0x565060, scale: 1.35, label: 'Bone Hearse' },
  },
  {
    // Grave Beacon output: temporary defenders that ignore Command but expire.
    // distinct from the deck AH-10 Ghoul Pack: conjured raiders, not commanded troops
    id: 'u-ah-grave-ghouls',
    name: 'Grave Ghouls',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['raider'],
    squadMemberCount: 5,
    maxHealth: 340, // TEST
    armor: 'none',
    moveSpeed: 4.2, // TEST — loping grave-things
    attack: { damage: 22, period: 0.9, range: 1.4 }, // TEST
    targetPriority: ['worker', 'squad', 'building'],
    abilities: [],
    command: 0, // temporary defender — ignores Command, strict active limit instead
    captureWeight: 0,
    ui: { color: 0x707a68, scale: 0.8, label: 'Grave Ghouls' },
  },
  {
    // Crimson Sovereign by-product: short-lived thralls raised mid-fight.
    id: 'u-ah-blood-thrall',
    name: 'Blood Thrall',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 3,
    maxHealth: 220, // TEST
    armor: 'none',
    moveSpeed: 3.8,
    attack: { damage: 18, period: 1.0, range: 1.4 }, // TEST
    targetPriority: ['squad', 'worker'],
    abilities: [],
    command: 0, // conjured, not commanded
    captureWeight: 0,
    ui: { color: 0x8a3040, scale: 0.75, label: 'Thrall' },
  },
  {
    // Fivefold Amalgam by-product: crawling flesh shed while it fights.
    id: 'u-ah-flesh-crawler',
    name: 'Flesh Crawler',
    faction: 'ashen-host',
    sizeTag: 'small',
    roleTags: ['frontline'],
    squadMemberCount: 3,
    maxHealth: 260, // TEST
    armor: 'none',
    moveSpeed: 3.2,
    attack: { damage: 20, period: 1.1, range: 1.4 }, // TEST
    targetPriority: ['squad', 'worker', 'building'],
    abilities: [],
    command: 0, // conjured, not commanded
    captureWeight: 0,
    ui: { color: 0x7a4a4a, scale: 0.7, label: 'Crawler' },
  },
  {
    // Apotheosis form A: army-combat Vampire Lord. Spec's flight is approximated as
    // very fast ground movement (no flying movement layer) — see README Deviations.
    id: 'u-ah-crimson-sovereign',
    name: 'Crimson Sovereign',
    faction: 'ashen-host',
    sizeTag: 'large',
    roleTags: ['boss', 'raider'],
    squadMemberCount: 1,
    maxHealth: 1200, // TEST — halved from previous prototype value per owner feedback; weak against concentrated ranged fire
    armor: 'light',
    moveSpeed: 4.8, // TEST — "fast flying movement" approximation
    attack: { damage: 95, period: 0.9, range: 2.0 }, // TEST
    targetPriority: ['squad', 'worker', 'siege'],
    abilities: ['ab-ah-sanguine-feast', 'ab-ah-call-blood-thralls', 'ab-ah-scent-of-ruin'],
    command: 9, // spec: consumes substantial Command
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0xa02838, scale: 1.7, label: 'Sovereign' },
  },
  {
    // Apotheosis form B: siege horror stitched from the five sacrificed workers.
    id: 'u-ah-fivefold-amalgam',
    name: 'Fivefold Amalgam',
    faction: 'ashen-host',
    sizeTag: 'giant',
    roleTags: ['boss', 'siege'],
    squadMemberCount: 1,
    maxHealth: 2700, // TEST — halved from previous prototype value per owner feedback; extremely high health
    armor: 'heavy',
    moveSpeed: 1.5, // spec: slow
    attack: { damage: 135, period: 1.9, range: 2.6, structureMult: 2.0 }, // TEST
    targetPriority: ['building', 'siege', 'squad'],
    abilities: ['ab-ah-rend-the-walls', 'ab-ah-shed-the-flesh'],
    command: 11, // spec: consumes substantial Command
    isBoss: true,
    captureWeight: 0.5,
    ui: { color: 0x6d4048, scale: 2.4, label: 'Amalgam' },
  },
];

// ---------------------------------------------------------------- abilities

export const AH_ABILITIES: AbilityDef[] = [
  {
    // bible AH-09 Hunger Route / Feed on Panic folded into one tempo trigger (deviation)
    id: 'ab-ah-hunger-route',
    name: 'Hunger Route',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 14,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'attackSpeed', mult: 1.2, duration: 6 }],
    ui: { label: 'Hunger', showFloatingLabel: true },
  },
  {
    // bible AH-10 Scavenge Flesh
    id: 'ab-ah-scavenge-flesh',
    name: 'Scavenge Flesh',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 12,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'heal', amount: 48 }],
    ui: { label: 'Scavenge', showFloatingLabel: false },
  },
  {
    // bible AH-11 Splinter the Weak
    id: 'ab-ah-splinter-the-weak',
    name: 'Splinter the Weak',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 10,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damage', mult: 1.2, duration: 5 }],
    ui: { label: 'Splinter the Weak', showFloatingLabel: false },
  },
  {
    // bible AH-12 Gather the Dead / Feed the Engine — the Carrier renders nearby death
    // into Essence on a cadence (transport loop simplified — deviation)
    id: 'ab-ah-gather-the-dead',
    name: 'Gather the Dead',
    trigger: { when: 'timer', period: 5 },
    internalCooldown: 5,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'consumeCorpseMarks', count: 1, radius: 8, healPer: 0, essencePer: 8 }],
    ui: { label: 'Gather the Dead', showFloatingLabel: false },
  },
  {
    // bible AH-13 Stitch Closed
    id: 'ab-ah-stitch-closed',
    name: 'Stitch Closed',
    trigger: { when: 'selfBelowHealth', fraction: 0.7, oncePerLife: false },
    internalCooldown: 16,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'consumeCorpseMarks', count: 1, radius: 8, healPer: 120, essencePer: 0 }],
    ui: { label: 'Stitch Closed', showFloatingLabel: true },
  },
  {
    // bible AH-14 Gore Rush
    id: 'ab-ah-gore-rush',
    name: 'Gore Rush',
    trigger: { when: 'targetIsolated', range: 8 },
    internalCooldown: 18,
    telegraphSeconds: 0.4,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 110 },
      { fx: 'knockback', distance: 3 },
    ],
    ui: { label: 'Gore Rush', showFloatingLabel: true },
  },
  {
    // bible AH-16 Blood Pressure (death-stack storage approximated as an every-3rd
    // structure strike — deviation)
    id: 'ab-ah-blood-pressure',
    name: 'Blood Pressure',
    trigger: { when: 'nthAttackVsStructure', n: 3 },
    internalCooldown: 2,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 260, repairHalfSeconds: 0 }],
    ui: { label: 'Blood Pressure', showFloatingLabel: true },
  },
  {
    // bible AH-16 Rupture Spray
    id: 'ab-ah-rupture-spray',
    name: 'Rupture Spray',
    trigger: { when: 'surroundedBy', count: 3, range: 5, sizeTags: ['small', 'medium'] },
    internalCooldown: 22,
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'areaDamage', amount: 95, radius: 5 }],
    ui: { label: 'Rupture Spray', showFloatingLabel: true },
  },
  {
    // bible AH-23 Carrion Dive
    id: 'ab-ah-carrion-dive',
    name: 'Carrion Dive',
    trigger: { when: 'targetIsolated', range: 9 },
    internalCooldown: 20,
    telegraphSeconds: 0.5,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 130 },
      { fx: 'knockback', distance: 4 },
    ],
    ui: { label: 'Carrion Dive', showFloatingLabel: true },
  },
  {
    // bible AH-23 Feast of Motion (stacking approximated by re-trigger)
    id: 'ab-ah-feast-of-motion',
    name: 'Feast of Motion',
    trigger: { when: 'allyDiedNearby', range: 12 },
    internalCooldown: 6,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [
      { fx: 'statMod', stat: 'moveSpeed', mult: 1.12, duration: 8 },
      { fx: 'statMod', stat: 'attackSpeed', mult: 1.12, duration: 8 },
    ],
    ui: { label: 'Feast of Motion', showFloatingLabel: true },
  },
  {
    // bible AH-24 Grave Quake
    id: 'ab-ah-grave-quake',
    name: 'Grave Quake',
    trigger: { when: 'surroundedBy', count: 3, range: 5 },
    internalCooldown: 20,
    telegraphSeconds: 0.6,
    targetSelector: 'self',
    effects: [{ fx: 'areaDamage', amount: 110, radius: 5 }],
    ui: { label: 'Grave Quake', showFloatingLabel: true },
  },
  {
    // bible AH-25 Court of the Fallen
    id: 'ab-ah-court-of-the-fallen',
    name: 'Court of the Fallen',
    trigger: { when: 'escortSquadDied' },
    internalCooldown: 4,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'spawnTemporary', unitId: 'u-ah-shade', count: 1, duration: 18 }],
    ui: { label: 'Court of the Fallen', showFloatingLabel: true },
  },
  {
    // bible AH-25 Empty Throne Decree
    id: 'ab-ah-empty-throne',
    name: 'Empty Throne Decree',
    trigger: { when: 'timer', period: 22 },
    internalCooldown: 22,
    telegraphSeconds: 0.5,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'statMod', stat: 'attackSpeed', mult: 0.75, duration: 6 },
      { fx: 'slow', magnitude: 0.25, duration: 6 },
    ],
    ui: { label: 'Empty Throne Decree', showFloatingLabel: true },
  },
  {
    // bible AH-25 King Without End
    id: 'ab-ah-king-without-end',
    name: 'King Without End',
    trigger: { when: 'selfBelowHealth', fraction: 0.25, oncePerLife: true },
    internalCooldown: 9999,
    telegraphSeconds: 0.6,
    targetSelector: 'self',
    effects: [
      { fx: 'consumeCorpseMarks', count: 99, radius: 14, healPer: 150, essencePer: 0 },
      { fx: 'areaDamage', amount: 90, radius: 8 },
    ],
    ui: { label: 'King Without End', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-04 Gather the Last Breath — charge-then-create-mark loop approximated
    // as converting a nearby corpse mark into Essence on each nearby death (deviation)
    id: 'ab-ah-gather-last-breath',
    name: 'Gather the Last Breath',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 10, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'consumeCorpseMarks', count: 1, radius: 10, healPer: 0, essencePer: 6 }], // TEST
    ui: { label: 'Gather the Last Breath', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AH-04 Wisp Screen — accuracy reduction approximated as a ranged-damage-taken aura
    id: 'ab-ah-wisp-screen',
    name: 'Wisp Screen',
    trigger: { when: 'targetedByRangedSquads', count: 2 },
    internalCooldown: 18, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'rangedDamageTaken', mult: 0.8, duration: 6 }], // TEST
    ui: { label: 'Wisp Screen', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): beast giants charge to close distance — charge extension
    id: 'ab-ah-leviathan-charge',
    name: 'Grave Surge',
    trigger: { when: 'enemyInRange', range: 18, minRange: 5 },
    internalCooldown: 30, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'charge', speedMult: 2.5, durationSeconds: 2.5, impactDamage: 120, impactSlowMult: 0.6, impactSlowSeconds: 2 }, // TEST
    ],
    ui: { label: 'Charge', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): humanoid boss giants hoist and crush small squads
    id: 'ab-ah-colossus-crush',
    name: 'Gravebound Grasp',
    trigger: { when: 'surroundedBy', count: 1, range: 6, sizeTags: ['small'] },
    internalCooldown: 25, // TEST
    telegraphSeconds: 0.8,
    targetSelector: 'self',
    effects: [{ fx: 'crushGrab', maxTargetSize: 'small', kills: 2, range: 6 }], // TEST
    ui: { label: 'Crush', showFloatingLabel: true },
  },
  {
    // owner request (not in bible): humanoid boss giants hoist and crush small squads
    id: 'ab-ah-king-crush',
    name: 'The King\'s Grasp',
    trigger: { when: 'surroundedBy', count: 1, range: 6, sizeTags: ['small'] },
    internalCooldown: 25, // TEST
    telegraphSeconds: 0.8,
    targetSelector: 'self',
    effects: [{ fx: 'crushGrab', maxTargetSize: 'small', kills: 2, range: 6 }], // TEST
    ui: { label: 'Crush', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-07 Sorrow Wave — cone attack-speed debuff + push-back approximated
    // against the triggering enemy only (deviation)
    id: 'ab-ah-sorrow-wave',
    name: 'Sorrow Wave',
    trigger: { when: 'surroundedBy', count: 3, range: 8 },
    internalCooldown: 16, // TEST
    telegraphSeconds: 0.5,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'statMod', stat: 'attackSpeed', mult: 0.75, duration: 5 }, // TEST
      { fx: 'knockback', distance: 2 }, // TEST
    ],
    ui: { label: 'Sorrow Wave', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-07 Haunting Bond — damage echo link approximated as a periodic
    // spectral strike + mark on the current target (deviation)
    id: 'ab-ah-haunting-bond',
    name: 'Haunting Bond',
    trigger: { when: 'timer', period: 12 },
    internalCooldown: 12,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'mark', markId: 'ah-haunting-bond', duration: 8 },
      { fx: 'damage', amount: 40 }, // TEST
    ],
    ui: { label: 'Haunting Bond', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-09 Terror Charge — full-distance-charge condition approximated as
    // striking the first small/medium squad they close on (deviation)
    id: 'ab-ah-terror-charge',
    name: 'Terror Charge',
    trigger: { when: 'surroundedBy', count: 1, range: 3, sizeTags: ['small', 'medium'] },
    internalCooldown: 14, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'triggeringEntity',
    effects: [
      { fx: 'damage', amount: 45 }, // TEST
      { fx: 'statMod', stat: 'attackSpeed', mult: 0.8, duration: 5 },
    ],
    ui: { label: 'Terror Charge', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-09 Ride Down the Weak (fleeing/feared conditions dropped — deviation)
    id: 'ab-ah-ride-down-the-weak',
    name: 'Ride Down the Weak',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 12, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'damage', mult: 1.25, duration: 5 }], // TEST
    ui: { label: 'Ride Down the Weak', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AH-12 Acid Rain — corrosive strip approximated as periodic corrosion
    // on the current target; the building-repair pressure lives in the attack's structureMult
    id: 'ab-ah-acid-rain',
    name: 'Acid Rain',
    trigger: { when: 'timer', period: 12 },
    internalCooldown: 12,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'damage', amount: 50 }, // TEST
      { fx: 'statMod', stat: 'damageTaken', mult: 1.15, duration: 6 }, // TEST — armor corrosion
    ],
    ui: { label: 'Acid Rain', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-12 Corpse Drop — grave-zone creation approximated: a wounded prey
    // raises a temporary shade as the dropped remnant (deviation)
    id: 'ab-ah-corpse-drop',
    name: 'Corpse Drop',
    trigger: { when: 'enemyBelowHealth', fraction: 0.3 },
    internalCooldown: 20, // TEST
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'spawnTemporary', unitId: 'u-ah-shade', count: 1, duration: 15 }], // TEST
    ui: { label: 'Corpse Drop', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-13 Contagion — stacking disease + infection pulse approximated as a
    // periodic area pulse around the target plus a healing penalty (deviation)
    id: 'ab-ah-contagion',
    name: 'Contagion',
    trigger: { when: 'timer', period: 10 },
    internalCooldown: 10,
    telegraphSeconds: 0.4,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'areaDamage', amount: 35, radius: 4 }, // TEST
      { fx: 'healReduction', mult: 0.75, duration: 8 }, // TEST
    ],
    ui: { label: 'Contagion', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-13 Unclean Miracle — save-from-death + permanent heal penalty
    // approximated as a death-triggered emergency heal of the weakest ally (deviation)
    id: 'ab-ah-unclean-miracle',
    name: 'Unclean Miracle',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 15, // TEST
    telegraphSeconds: 0.5,
    targetSelector: 'self',
    effects: [{ fx: 'healLowestAlly', amount: 110, range: 10 }], // TEST
    ui: { label: 'Unclean Miracle', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-14 Decapitate
    id: 'ab-ah-decapitate',
    name: 'Decapitate',
    trigger: { when: 'enemyBelowHealth', fraction: 0.35 },
    internalCooldown: 12, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'damage', amount: 95 }], // TEST
    ui: { label: 'Decapitate', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-14 No Return — recovery-delay/anti-resurrection approximated as a
    // heavy healing reduction on wounded targets (deviation)
    id: 'ab-ah-no-return',
    name: 'No Return',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 10, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'triggeringEntity',
    effects: [{ fx: 'healReduction', mult: 0.4, duration: 8 }], // TEST
    ui: { label: 'No Return', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AH-15 Ashstep — burning patch approximated as an instant ember burst
    // at the departure point (deviation); 18 s cooldown is bible-stated
    id: 'ab-ah-ashstep',
    name: 'Ashstep',
    trigger: { when: 'chargedBy', sizeTags: ['small', 'medium', 'large', 'giant'] },
    internalCooldown: 18, // bible v0.2.4 AH-15
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [
      { fx: 'teleport', distance: 5, direction: 'backward' }, // TEST distance
      { fx: 'areaDamage', amount: 40, radius: 2 }, // TEST
    ],
    ui: { label: 'Ashstep', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-15 Immolate the Remains — on-death explosion approximated as a
    // once-per-life detonation at death's door; the corpse-mark condition is dropped (deviation)
    id: 'ab-ah-immolate-remains',
    name: 'Immolate the Remains',
    trigger: { when: 'selfBelowHealth', fraction: 0.15, oncePerLife: true },
    internalCooldown: 9999,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'areaDamage', amount: 75, radius: 3 }], // TEST
    ui: { label: 'Immolate the Remains', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-16 Corpse Wake — grave-zone trail approximated as a periodic
    // grave-empowerment aura for nearby Ashen squads (deviation)
    id: 'ab-ah-corpse-wake',
    name: 'Corpse Wake',
    trigger: { when: 'timer', period: 10 },
    internalCooldown: 10,
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'auraBuff', radius: 8, stat: 'moveSpeed', mult: 1.1, duration: 8 }], // TEST
    ui: { label: 'Corpse Wake', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AH-16 Swallow the Faithful — devour-and-expel approximated as a heavy
    // bite that pins the current target; allied-sacrifice option dropped (deviation)
    id: 'ab-ah-swallow-the-faithful',
    name: 'Swallow the Faithful',
    trigger: { when: 'timer', period: 24 }, // bible v0.2.4 AH-16: every 24 s
    internalCooldown: 24,
    telegraphSeconds: 0.8,
    targetSelector: 'currentTarget',
    effects: [
      { fx: 'damage', amount: 170 }, // TEST
      { fx: 'root', duration: 3 }, // TEST — swallowed
    ],
    ui: { label: 'Swallow the Faithful', showFloatingLabel: true },
  },
  {
    // bible v0.2.4 AH-17 Reform the Ranks — member reformation folded into squad-pool healing
    id: 'ab-ah-reform-the-ranks',
    name: 'Reform the Ranks',
    trigger: { when: 'allyDiedNearby', range: 10 },
    internalCooldown: 8, // TEST
    telegraphSeconds: 0.3,
    targetSelector: 'self',
    effects: [{ fx: 'heal', amount: 70 }], // TEST
    ui: { label: 'Reform the Ranks', showFloatingLabel: false },
  },
  {
    // bible v0.2.4 AH-17 Eternal March — objective-direction condition and fear immunity
    // approximated as a periodic march-speed pulse (deviation)
    id: 'ab-ah-eternal-march',
    name: 'Eternal March',
    trigger: { when: 'timer', period: 20 },
    internalCooldown: 20,
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'moveSpeed', mult: 1.15, duration: 10 }], // TEST duration
    ui: { label: 'Eternal March', showFloatingLabel: false },
  },
  // ---- Core Annex System: Apotheosis forms (owner spec 2026-07-13) --------
  {
    // Crimson Sovereign "strong lifesteal" approximated as a self-heal that pulses
    // while in melee contact (no on-hit drain effect) — see README Deviations
    id: 'ab-ah-sanguine-feast',
    name: 'Sanguine Feast',
    trigger: { when: 'enemyInRange', range: 2.5 },
    internalCooldown: 4, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'heal', amount: 110 }], // TEST
    ui: { label: 'Sanguine Feast', showFloatingLabel: false },
  },
  {
    // spec: "periodically creates temporary Blood Thralls from enemy deaths" —
    // enemy-death triggers don't exist; approximated as thralls torn from wounded
    // enemies mid-fight — see README Deviations
    id: 'ab-ah-call-blood-thralls',
    name: 'Call of the Vein',
    trigger: { when: 'enemyBelowHealth', fraction: 0.5 },
    internalCooldown: 24, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'self',
    effects: [{ fx: 'spawnTemporary', unitId: 'u-ah-blood-thrall', count: 2, duration: 20 }], // TEST
    ui: { label: 'Blood Thralls', showFloatingLabel: true },
  },
  {
    // spec: "gains attack speed while fighting near wounded units"
    id: 'ab-ah-scent-of-ruin',
    name: 'Scent of Ruin',
    trigger: { when: 'enemyBelowHealth', fraction: 0.6 },
    internalCooldown: 10, // TEST
    telegraphSeconds: 0.2,
    targetSelector: 'self',
    effects: [{ fx: 'statMod', stat: 'attackSpeed', mult: 1.3, duration: 6 }], // TEST
    ui: { label: 'Scent of Ruin', showFloatingLabel: false },
  },
  {
    // spec: "tears armor from buildings" — no armor-strip effect; approximated as a
    // recurring heavy structure strike with repair halving — see README Deviations
    id: 'ab-ah-rend-the-walls',
    name: 'Rend the Walls',
    trigger: { when: 'nthAttackVsStructure', n: 3 },
    internalCooldown: 8, // TEST
    telegraphSeconds: 0.6,
    targetSelector: 'currentTarget',
    effects: [{ fx: 'structureStrike', damage: 320, repairHalfSeconds: 3 }], // TEST
    ui: { label: 'Rend the Walls', showFloatingLabel: true },
  },
  {
    // spec: "periodically sheds crawling flesh minions"
    id: 'ab-ah-shed-the-flesh',
    name: 'Shed the Flesh',
    trigger: { when: 'timer', period: 22 }, // TEST
    internalCooldown: 22,
    telegraphSeconds: 0.4,
    targetSelector: 'self',
    effects: [{ fx: 'spawnTemporary', unitId: 'u-ah-flesh-crawler', count: 2, duration: 25 }], // TEST
    ui: { label: 'Shed the Flesh', showFloatingLabel: true },
  },
];

// ---------------------------------------------------------------- buildings

export const AH_BUILDINGS: BuildingDef[] = [
  {
    id: 'b-ah-ossuary-throne',
    name: 'Ossuary Throne',
    faction: 'ashen-host',
    kind: 'core',
    maxHealth: 3650, // bible 6,900 × owner's ~0.53 TTK factor
    armor: 'moderate',
    footprint: { w: 8, h: 8 },
    isMajorStructure: true,
    buildTime: 0,
    placement: 'core-territory',
    attack: {
      damage: 95, // bible AH-01 Ashen Bolts
      period: 2.2,
      range: 20,
      projectileSpeed: 22,
      targetPriority: ['squad', 'large', 'giant', 'siege'],
    },
    // Core Annex System (owner spec 2026-07-13): three permanent sockets, all
    // eventually buildable — the strategic choice is build ORDER. Replaces the
    // v0.2 Dread Spire / Corpse Furnace / Ebon Reliquary track (README Deviations).
    upgrades: [
      {
        id: 'up-ah-sanguine-crucible',
        name: 'Sanguine Crucible',
        icon: '🩸',
        socket: 'top',
        cost: { power: 240, essence: 60 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Blood Tithe (toggle): sacrifices one unsheltered worker every 30 s. At 5 Souls Offered, a targetable Blood Cocoon gestates 120 s into a Crimson Sovereign or Fivefold Amalgam. Once per match.',
        bloodTithe: {
          period: 30, // spec
          minWorkers: 6, // spec: pauses below six total workers
          warnSeconds: 5, // spec
          soulsRequired: 5, // spec
          cocoonBuildingId: 'b-ah-blood-cocoon',
          apotheosisSeconds: 120, // spec
          forms: [
            { form: 'amalgam', unitId: 'u-ah-fivefold-amalgam' },
            { form: 'sovereign', unitId: 'u-ah-crimson-sovereign' },
          ],
        },
      },
      {
        id: 'up-ah-ossuary-ledger',
        name: 'Ossuary Ledger',
        icon: '📜',
        socket: 'left',
        cost: { power: 190, essence: 25 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          '+4 Workforce, +3 shelter; workers train 25% faster. Each deliberate sacrifice grants Grim Industry: +4% gathering for 40 s, stacking 5 times.',
        effects: [
          { kind: 'workforceBonus', amount: 4 }, // spec
          { kind: 'shelterBonus', amount: 3 }, // spec
          { kind: 'workerTrainSpeedMult', mult: 1.25 }, // spec
          { kind: 'grimIndustry', gatherMult: 1.04, duration: 40, maxStacks: 5 }, // spec
        ],
      },
      {
        id: 'up-ah-carrion-procession',
        name: 'Carrion Procession',
        icon: '🔔',
        socket: 'bottom',
        cost: { power: 260, essence: 40 }, // spec
        buildTime: 15, // owner request 2026-07-13
        rulesText:
          'Produces a Bone Hearse every 105 s. E: Raise Grave Beacon plants a free forward summoning tower that turns nearby deaths into raiding Ghoul Packs.',
        spawner: { unitId: 'u-ah-bone-hearse', period: 105, maxAlive: 1 }, // spec
      },
    ],
    ui: { color: 0x6a5d70, label: 'Ossuary Throne' },
  },
  {
    // Core Annex System: targetable Apotheosis vessel. If destroyed mid-gestation the
    // five sacrificed workers stay lost and the Apotheosis fails (once per match).
    id: 'b-ah-blood-cocoon',
    name: 'Blood Cocoon',
    faction: 'ashen-host',
    kind: 'defense',
    maxHealth: 2200, // TEST — counterplay window during the 120 s transformation
    armor: 'light',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'core-territory',
    ui: { color: 0x7a2a38, label: 'Blood Cocoon' },
  },
  {
    // Core Annex System: free forward grave tower raised from a Bone Hearse.
    id: 'b-ah-grave-beacon',
    name: 'Grave Beacon',
    faction: 'ashen-host',
    kind: 'defense',
    maxHealth: 1000, // TEST
    armor: 'moderate',
    footprint: { w: 4, h: 4 },
    isMajorStructure: false,
    buildTime: 50, // spec: rises over 50 s
    placement: 'both',
    selfBuilding: true, // spec: free deployed buildings need no workers
    summonAnchor: { maxAscension: 1, radius: 14 }, // spec: Ashen forward summoning point
    spawner: {
      unitId: 'u-ah-grave-ghouls',
      period: 80, // spec: every 80 s if it has absorbed enough deaths
      count: 1,
      temporarySeconds: 45, // TEST — temporary defenders with strict limits
      command: 'raid', // spec: automatically attack the nearest enemy-controlled site
      corpseCost: 4, // TEST — "absorbed enough deaths"
      corpseAbsorbRadius: 16, // TEST
    },
    ui: { color: 0x585264, label: 'Grave Beacon' },
  },
  {
    id: 'b-ah-site-tower',
    name: 'Bone Spire',
    faction: 'ashen-host',
    kind: 'defense',
    maxHealth: 1500,
    armor: 'moderate',
    footprint: { w: 3, h: 3 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'site-radius',
    producesWorkers: true,
    isSiteTower: true,
    signatureText: 'Marrow Lash applies Brittle; nearby enemy deaths repair the Spire.',
    traits: [{ kind: 'deathSustain', radius: 14, heal: 45 }],
    attack: {
      damage: 32,
      period: 1.1,
      range: 15,
      projectileSpeed: 24,
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    attackRequiresUpgrade: true,
    upgrade: {
      id: 'up-ah-marrow-lash',
      name: 'Marrow Lash',
      cost: { power: 150, essence: 0 },
      rulesText: 'Arms the spire with a bone lash that automatically strikes nearby enemies.',
    },
    ui: { color: 0x8a8090, label: 'Bone Spire' },
  },
  {
    id: 'b-ah-wailing-obelisk',
    name: 'Wailing Obelisk',
    faction: 'ashen-host',
    kind: 'defense',
    maxHealth: 1050, // TEST
    armor: 'light',
    footprint: { w: 3, h: 3 },
    isMajorStructure: true,
    buildTime: 30, // TEST
    placement: 'both',
    signatureText: 'Lament Pulse fears nearby enemies every 30 s; nearby deaths sustain the Obelisk and hasten its next pulse.',
    traits: [
      { kind: 'periodicControl', radius: 11, cooldown: 30, effect: 'fear', duration: 4 },
      { kind: 'deathSustain', radius: 14, heal: 20, cooldownReduction: 2 },
    ],
    attack: {
      damage: 42, // TEST
      period: 1.35,
      range: 15,
      projectileSpeed: 22,
      targetPriority: ['squad', 'worker', 'large', 'giant'],
    },
    upgrade: {
      id: 'up-ah-choir-of-the-damned',
      name: 'Choir of the Damned',
      cost: { power: 180, essence: 55 }, // TEST
      rulesText: 'Feared units also suffer reduced healing and recovery for 8 s.',
    },
    ui: { color: 0x694b78, label: 'Wailing Obelisk' },
  },
  {
    id: 'b-ah-castle-wall',
    name: 'Cairn Wall',
    faction: 'ashen-host',
    kind: 'wall',
    maxHealth: 1400,
    armor: 'heavy',
    footprint: { w: 2, h: 12 },
    isMajorStructure: false,
    buildTime: 30, // TEST — owner: walls take a while to raise and are fragile mid-build
    placement: 'both',
    selfBuilding: true,
    mountSlots: 2,
    signatureText: 'Bone Harvest repairs the wall from nearby deaths; excess healing becomes temporary armor.',
    traits: [{ kind: 'deathSustain', radius: 12, heal: 60, shieldCap: 300 }],
    ui: { color: 0x6a6d76, label: 'Cairn Wall' },
  },
  {
    id: 'b-ah-ritual-standard',
    name: 'Eclipse Brazier',
    faction: 'ashen-host',
    kind: 'defense',
    maxHealth: 500,
    armor: 'none',
    footprint: { w: 2, h: 2 },
    isMajorStructure: false,
    buildTime: 0,
    placement: 'both',
    signatureText: 'Funeral Fuel turns nearby friendly deaths into a shield while the ritual channels.',
    traits: [{ kind: 'deathShield', radius: 12, shield: 75, maxShield: 400 }],
    ui: { color: 0x77d97e, label: 'Eclipse Brazier' },
  },
  {
    id: 'b-ah-grave-hatchery',
    name: 'Grave Hatchery',
    faction: 'ashen-host',
    kind: 'production',
    maxHealth: 1150, // bible AH-05
    armor: 'light',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 29,
    placement: 'both',
    signatureText: 'Carrion Incubation: each nearby death removes time from the active queue, up to 10 s per squad.',
    traits: [{ kind: 'deathProduction', radius: 16, secondsPerDeath: 1.5, maxSecondsPerQueueItem: 10 }],
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-ah-ghoul-pack', cost: { power: 80, essence: 0, command: 2 }, trainTime: 20, ascension: 1 },
        { unitId: 'u-ah-bone-archers', cost: { power: 105, essence: 0, command: 3 }, trainTime: 25, ascension: 1 },
        // v0.2.4 additions — bone broods hatch here (broad-roster hub)
      ],
    },
    upgrade: {
      id: 'up-ah-ravenous-brood',
      name: 'Ravenous Brood',
      cost: { power: 150, essence: 25 },
      rulesText: 'After ten nearby deaths, the next squad arrives with a temporary reinforcement brood.',
    },
    ui: { color: 0x7a6d80, label: 'Grave Hatchery' },
  },
  {
    id: 'b-ah-blood-foundry',
    name: 'Blood Foundry',
    faction: 'ashen-host',
    kind: 'production',
    maxHealth: 1350, // bible AH-06
    armor: 'moderate',
    footprint: { w: 5, h: 5 },
    isMajorStructure: true,
    buildTime: 42,
    placement: 'both',
    cruelProduction: true,
    signatureText: 'Cruel Production sacrifices a worker to cut 25 s from the active queue and armor the resulting Gore Engine.',
    roster: {
      queueSize: 5,
      entries: [
        { unitId: 'u-ah-gore-engine', cost: { power: 250, essence: 75, command: 8 }, trainTime: 47, ascension: 3 },
        // v0.2.4 additions — heavy flesh and killing engines grown in the vats
      ],
    },
    upgrade: {
      id: 'up-ah-refined-agony',
      name: 'Refined Agony',
      cost: { power: 200, essence: 60 },
      rulesText: 'Sacrificed vitality becomes a larger Blood Armor shield and charges the Gore Engine opening attack.',
    },
    ui: { color: 0x8a5a5a, label: 'Blood Foundry' },
  },
  {
    id: 'b-ah-soul-mill',
    name: 'Soul Mill',
    faction: 'ashen-host',
    kind: 'production',
    maxHealth: 1000, // bible AH-07
    armor: 'light',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 36,
    placement: 'both',
    corpseConversion: { radius: 22, threshold: 6, essence: 25 },
    dropOff: ['essence'],
    signatureText: 'Soul Overdraft consumes stored Corpse Marks for a gathering surge; the upgrade raises defensive Shades.',
    roster: {
      queueSize: 3,
      entries: [
        { unitId: 'u-ah-soul-collector', cost: { power: 95, essence: 0 }, trainTime: 28, ascension: 2 },
      ],
    },
    upgrade: {
      id: 'up-ah-mill-of-echoes',
      name: 'Mill of Echoes',
      cost: { power: 170, essence: 45 },
      rulesText: 'Every tenth Corpse Mark processed creates a temporary Shade to defend the Mill.',
    },
    ui: { color: 0x77a0a8, label: 'Soul Mill' },
  },
  {
    // bible AH-21: jawed barrier + sacrifice site (bite approximated by gate marks)
    id: 'b-ah-devouring-gate',
    name: 'Devouring Gate',
    faction: 'ashen-host',
    kind: 'trap-structure',
    maxHealth: 900,
    armor: 'moderate',
    footprint: { w: 2, h: 8 },
    isMajorStructure: false,
    buildTime: 20,
    placement: 'both',
    selfBuilding: true,
    gate: { marks: 2, markDuration: 10, damageBonus: 1.2, stunSeconds: 1.5, radius: 6 },
    signatureText: 'Maw Snap roots the first squad crossing every 25 s; friendly sacrifices feed the next boss.',
    traits: [{ kind: 'periodicControl', radius: 6, cooldown: 25, effect: 'root', duration: 2 }],
    ui: { color: 0x6a4d5a, label: 'Devouring Gate' },
  },
  {
    id: 'b-ah-rift-altar',
    name: 'Rift Altar',
    faction: 'ashen-host',
    kind: 'production',
    maxHealth: 1500, // bible AH-08
    armor: 'moderate',
    footprint: { w: 4, h: 4 },
    isMajorStructure: true,
    buildTime: 48,
    placement: 'both',
    summonAnchor: { maxAscension: 4, radius: 14 }, // Deathway forward summoning
    signatureText: 'Reaving Passage relocates a squad from the Core; the Altar unlocks forward summons but trains no units.',
        // v0.2.4 additions — spectral assault package projected through the veil
    upgrade: {
      id: 'up-ah-widen-the-wound',
      name: 'Widen the Wound',
      cost: { power: 230, essence: 85 },
      rulesText: 'Increases the summoning radius and gives newly summoned units temporary lifesteal.',
    },
    ui: { color: 0x6a4d80, label: 'Rift Altar' },
  },
];

// ---------------------------------------------------------------- spells / traps / rituals

export const AH_SPELLS: SpellDef[] = [
  {
    // bible AH-17
    id: 'sp-ah-reckoning',
    name: 'Reckoning',
    faction: 'ashen-host',
    targeting: { shape: 'entity', validTargets: 'friendly-squad', range: 'global' },
    effects: [],
    sacrificeBlast: { fraction: 0.35, radius: 6 },
  },
  {
    // bible AH-18
    id: 'sp-ah-last-battalion',
    name: 'Last Battalion',
    faction: 'ashen-host',
    targeting: { shape: 'circle', radius: 4, range: 'global' },
    effects: [],
    temporarySummon: { units: ['u-ah-ghoul-pack', 'u-ah-bone-archers'], duration: 45 },
  },
  {
    // bible AH-19 (temporary-extension and boss stacking simplified out — deviation)
    id: 'sp-ah-black-harvest',
    name: 'Black Harvest',
    faction: 'ashen-host',
    targeting: { shape: 'circle', radius: 14, range: 'global' },
    zoneDuration: 20,
    effects: [],
    deathHarvest: { allyPower: 15, enemyEssence: 10 },
  },
];

export const AH_TRAPS: TrapDef[] = [
  {
    // bible AH-20
    id: 'tr-ah-seal-of-rot',
    name: 'Seal of Rot',
    faction: 'ashen-host',
    behavior: 'rootRing',
    pieceHealth: 300,
    perDeploymentPieces: 1,
    buildTime: 8, // TEST
    triggerRadius: 5,
    triggerSizeTags: ['small', 'medium', 'large'],
    effects: [{ fx: 'healReduction', mult: 0.65, duration: 14 }],
    maxActiveFields: 2,
    minSquads: 2,
  },
];

export const AH_RITUALS: RitualDef[] = [
  {
    // bible AH-22
    id: 'rit-ah-night-of-the-unburied',
    name: 'Night of the Unburied',
    faction: 'ashen-host',
    channelSeconds: 11,
    channelAt: 'both',
    standardHealth: 550, // TEST
    refundFraction: 0.35,
  },
];

export const AH_BOSS_EXTRAS: Record<string, BossExtras> = {
  'boss-ah-veyr': { summonSeconds: 15, announcement: 'local' },
  'boss-ah-gravebound-colossus': {
    summonSeconds: 30, // owner request 2026-07-13
    announcement: 'global',
    summonSacrifice: true, // bible AH-24: assembled by destroying a friendly squad
  },
  'boss-ah-hollow-king': {
    summonSeconds: 60, // owner request 2026-07-13
    announcement: 'global',
  },
};

// ---------------------------------------------------------------- cards (deck pool)

export const AH_CARDS: CardDef[] = [
  {
    id: 'card-ah-grave-hatchery',
    name: 'Grave Hatchery',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-grave-hatchery',
    cost: { power: 165, essence: 0 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 55 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '⚰',
      roleLine: 'Cheap swarm production',
      rulesText: 'Hatches Ghoul Packs and Bone Archers only. Nearby deaths accelerate the active queue through Carrion Incubation.',
    },
  },
  {
    id: 'card-ah-blood-foundry',
    name: 'Blood Foundry',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-blood-foundry',
    cost: { power: 245, essence: 35 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 70 },
    hotkey: '',
    ui: {
      icon: '🩸',
      roleLine: 'Heavy sacrifice production',
      rulesText:
        'Produces only Gore Engines. Cruel Production sacrifices one worker to cut 25 s from the queue and grant Blood Armor; disabled at five workers.',
    },
  },
  {
    id: 'card-ah-soul-mill',
    name: 'Soul Mill',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-soul-mill',
    cost: { power: 210, essence: 0 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 60 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🌀',
      roleLine: 'Corpse-to-Essence economy',
      rulesText: 'Every 6 Corpse Marks near the Mill become 25 Essence. Trains Soul Collectors (Asc II).',
    },
  },
  {
    id: 'card-ah-rift-altar',
    name: 'Rift Altar',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-rift-altar',
    cost: { power: 300, essence: 100 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 80 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '🕳',
      roleLine: 'Forward summon network',
      rulesText: 'Forward summoning anchor for Ashen cards and bosses. It no longer trains military units.',
    },
  },
  {
    id: 'card-ah-reavers',
    name: 'Gravebound Reavers',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-reavers',
    cost: { power: 105, essence: 0, command: 3 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 30 },
    hotkey: '',
    ui: {
      icon: '🗡',
      roleLine: 'Opening raid summon',
      rulesText: 'Seven fast undead. Hunger for wounded prey; always leave a Corpse Mark when destroyed.',
    },
  },
  {
    // bible v0.2.4 AH-04
    id: 'card-ah-soul-lanterns',
    name: 'Soul Lanterns',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-soul-lanterns',
    cost: { power: 95, essence: 10, command: 2 },
    ascension: 1,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 40 },
    maxActive: 3,
    hotkey: '',
    ui: {
      icon: '🏮',
      roleLine: 'Corpse collection & concealment',
      rulesText: 'Three lantern-bearers render nearby deaths into Essence and screen allies from ranged volleys.',
    },
  },
  {
    // bible v0.2.4 AH-07
    id: 'card-ah-wailing-brides',
    name: 'Wailing Brides',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-wailing-brides',
    cost: { power: 150, essence: 25, command: 4 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 56 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '👰',
      roleLine: 'Fear & damage amplification',
      rulesText: 'Four spectral casters. Crowds are pushed back and slowed; a red thread haunts their chosen victim.',
    },
  },
  {
    // bible v0.2.4 AH-09
    id: 'card-ah-dread-cavalry',
    name: 'Dread Cavalry',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-dread-cavalry',
    cost: { power: 185, essence: 25, command: 5 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 64 },
    hotkey: '',
    ui: {
      icon: '🐎',
      roleLine: 'Fear charge & backline disruption',
      rulesText: 'Four skeletal riders. Their charge staggers light squads and they ride down anything already wounded.',
    },
  },
  {
    // bible v0.2.4 AH-12
    id: 'card-ah-bonewyrm-riders',
    name: 'Bonewyrm Riders',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-bonewyrm-riders',
    cost: { power: 235, essence: 70, command: 6 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 88 },
    hotkey: '',
    ui: {
      icon: '🐉',
      roleLine: 'Fast harassment & grave seeding',
      rulesText: 'Three wyrm riders skirt the battle to melt armor with acid, and wounded prey rises behind them as shades.',
    },
  },
  {
    // bible v0.2.4 AH-13
    id: 'card-ah-plague-apostles',
    name: 'Plague Apostles',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-plague-apostles',
    cost: { power: 190, essence: 65, command: 4 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 72 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🦠',
      roleLine: 'Contagion & anti-recovery',
      rulesText: 'Three robed Apostles spread pulsing disease through packed lines and stitch dying allies back together.',
    },
  },
  {
    // bible v0.2.4 AH-14
    id: 'card-ah-ebon-executioners',
    name: 'Ebon Executioners',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-ebon-executioners',
    cost: { power: 210, essence: 55, command: 5 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 74 },
    hotkey: '',
    ui: {
      icon: '🪓',
      roleLine: 'Recovery denial & elite killing',
      rulesText: 'Four axe-bearing revenants. Wounded squads are decapitated and what they fell struggles to recover.',
    },
  },
  {
    // bible v0.2.4 AH-15
    id: 'card-ah-cinder-revenants',
    name: 'Cinder Revenants',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-cinder-revenants',
    cost: { power: 195, essence: 60, command: 4 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 70 },
    hotkey: '',
    ui: {
      icon: '🔥',
      roleLine: 'Phase skirmish & self-immolation',
      rulesText: 'Five ash soldiers blink out of melee in a burst of embers — and detonate when death finally catches them.',
    },
  },
  {
    // bible v0.2.4 AH-16
    id: 'card-ah-grave-leviathan',
    name: 'Grave Leviathan',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-grave-leviathan',
    cost: { power: 340, essence: 130, command: 10 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 108 },
    hotkey: '',
    ui: {
      icon: '🪱',
      roleLine: 'Army disruptor & corpse engine',
      rulesText: 'A giant corpse-worm. Its wake empowers the Host and every 24 s it swallows whatever it is fighting.',
    },
  },
  {
    // bible v0.2.4 AH-17
    id: 'card-ah-deathless-legion',
    name: 'Deathless Legion',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-deathless-legion',
    cost: { power: 320, essence: 140, command: 8 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 104 },
    hotkey: '',
    ui: {
      icon: '🛡',
      roleLine: 'Reforming endgame phalanx',
      rulesText: 'Eight ancient soldiers. Every nearby death rebuilds their ranks and their march never slows.',
    },
  },
  {
    id: 'card-ah-reckoning',
    name: 'Reckoning',
    faction: 'ashen-host',
    category: 'spell',
    subjectId: 'sp-ah-reckoning',
    cost: { power: 85, essence: 0 },
    ascension: 1,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'reusable', cooldown: 50 },
    hotkey: '',
    ui: {
      icon: '💥',
      roleLine: 'Sacrifice damage combo',
      rulesText: 'Sacrifice one of your squads: nearby enemies take damage equal to 35% of its maximum health.',
    },
  },
  {
    id: 'card-ah-last-battalion',
    name: 'Last Battalion',
    faction: 'ashen-host',
    category: 'spell',
    subjectId: 'sp-ah-last-battalion',
    cost: { power: 180, essence: 35 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 80 },
    hotkey: '',
    ui: {
      icon: '👥',
      roleLine: 'Temporary comeback force',
      rulesText: 'Summons a temporary Ghoul Pack and Bone Archer squad for 45 s. Natural expiry leaves no corpses.',
    },
  },
  {
    id: 'card-ah-black-harvest',
    name: 'Black Harvest',
    faction: 'ashen-host',
    category: 'spell',
    subjectId: 'sp-ah-black-harvest',
    cost: { power: 180, essence: 160 },
    ascension: 4,
    summonLocations: ['anywhere-friendly'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🌑',
      roleLine: 'Ultimate harvest zone',
      rulesText:
        'Once per match. For 20 s, every death in the zone pays the Host: your losses return Power, enemy losses return Essence.',
    },
  },
  {
    id: 'card-ah-seal-of-rot',
    name: 'Seal of Rot',
    faction: 'ashen-host',
    category: 'trap',
    subjectId: 'tr-ah-seal-of-rot',
    cost: { power: 115, essence: 15 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 65 },
    maxActive: 2,
    hotkey: '',
    ui: {
      icon: '🪦',
      roleLine: 'Anti-healing rune',
      rulesText: 'Triggers when 2+ enemy squads enter: their healing and recovery is reduced 35% for 14 s.',
    },
  },
  {
    id: 'card-ah-devouring-gate',
    name: 'Devouring Gate',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-devouring-gate',
    cost: { power: 190, essence: 50 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 2, cooldown: 60 },
    maxActive: 1,
    hotkey: '',
    ui: {
      icon: '👄',
      roleLine: 'Jawed barrier & sacrifice site',
      rulesText:
        'Marks enemies that force passage (+20% damage to marked). Sacrifice squads here to discount your next boss 12% per offering (max 2).',
    },
  },
  {
    id: 'card-ah-night-unburied',
    name: 'Night of the Unburied',
    faction: 'ashen-host',
    category: 'ritual',
    subjectId: 'rit-ah-night-of-the-unburied',
    cost: { power: 250, essence: 120 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🌘',
      roleLine: 'Mass resurrection ritual',
      rulesText:
        'Channel 11 s. Your 4 most recently fallen squads return as spectres (55% health, 40 s). Specialists return as shades.',
    },
  },
  {
    id: 'card-ah-veyr',
    name: 'Carrion Prince Veyr',
    faction: 'ashen-host',
    category: 'boss',
    subjectId: 'boss-ah-veyr',
    cost: { power: 245, essence: 45, command: 7 },
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🦇',
      roleLine: 'Roaming raid boss',
      rulesText: 'Once per match. Feasts on nearby death for speed; dives onto isolated ranged squads and workers.',
    },
  },
  {
    id: 'card-ah-colossus',
    name: 'Gravebound Colossus',
    faction: 'ashen-host',
    category: 'boss',
    subjectId: 'boss-ah-gravebound-colossus',
    cost: { power: 410, essence: 135, command: 13 },
    ascension: 3,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '🦴',
      roleLine: 'Sacrifice-assembled Giant',
      rulesText:
        'Once per match. Assembly consumes a friendly non-boss squad near the summon point. Grave Quake punishes anyone who surrounds it.',
    },
  },
  {
    id: 'card-ah-hollow-king',
    name: 'The Hollow King',
    faction: 'ashen-host',
    category: 'boss',
    subjectId: 'boss-ah-hollow-king',
    cost: { power: 590, essence: 280, command: 19 },
    ascension: 4,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'once' },
    hotkey: '',
    ui: {
      icon: '👑',
      roleLine: 'Ascension IV commander',
      rulesText:
        'Once per match. 60 s announced arrival. Fallen escorts return as shades; chains the strongest enemy; devours corpses at death’s door.',
    },
  },
  {
    id: 'card-ah-flesh-guard',
    name: 'Flesh Guard',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-flesh-guard',
    cost: { power: 150, essence: 20, command: 4 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 55 }, // TEST
    hotkey: '',
    ui: { icon: '🛡', roleLine: 'Bodyguard', rulesText: 'Durable stitched infantry for holding violent ground.' },
  },
  {
    id: 'card-ah-corpse-carrier',
    name: 'Corpse Carrier',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-corpse-carrier',
    cost: { power: 125, essence: 15, command: 3 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 3, cooldown: 50 }, // TEST
    hotkey: '',
    ui: { icon: '⚰', roleLine: 'Limited corpse-logistics summon', rulesText: 'No longer trained at the Hatchery. A limited direct summon for carrying death-powered momentum forward.' },
  },
  {
    id: 'card-ah-sacrificial-brute',
    name: 'Sacrificial Brute',
    faction: 'ashen-host',
    category: 'unit',
    subjectId: 'u-ah-sacrificial-brute',
    cost: { power: 135, essence: 0, command: 4 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'limited', maxUses: 3, cooldown: 48 }, // TEST
    hotkey: '',
    ui: { icon: '🩸', roleLine: 'Disposable shock monster', rulesText: 'A brutal momentum piece designed to be spent.' },
  },
  {
    id: 'card-ah-wailing-obelisk',
    name: 'Wailing Obelisk',
    faction: 'ashen-host',
    category: 'building',
    subjectId: 'b-ah-wailing-obelisk',
    cost: { power: 205, essence: 45 }, // TEST
    ascension: 2,
    summonLocations: ['core', 'controlled-site'],
    useModel: { kind: 'reusable', cooldown: 65 }, // TEST
    maxActive: 3,
    hotkey: '',
    ui: { icon: '🗿', roleLine: 'Death-fed fear tower', rulesText: 'Modest spell bolts and a recurring fear pulse; nearby deaths repair it and hasten the next lament.' },
  },
];

/** Bible §8.2 "The Kingdom of Useful Death" test deck (slot order = hotkey order). */
export const AH_DEFAULT_DECK: string[] = [
  'card-ah-grave-hatchery',
  'card-ah-blood-foundry',
  'card-ah-soul-mill',
  'card-ah-rift-altar',
  'card-ah-reavers',
  'card-ah-reckoning',
  'card-ah-last-battalion',
  'card-ah-black-harvest',
  'card-ah-seal-of-rot',
  'card-ah-devouring-gate',
  'card-ah-night-unburied',
  'card-ah-veyr',
  'card-ah-colossus',
  'card-ah-hollow-king',
];
